/**
 * ConnectionMonitor — active connectivity tracking with health-check polling.
 *
 * navigator.onLine only detects if a network interface exists — it doesn't
 * tell you if the API is actually reachable. This module pings the health
 * endpoint periodically and tracks true connectivity state.
 *
 * Also maintains an offline operation queue: failed cloud uploads are
 * queued in IndexedDB and retried automatically when connectivity returns.
 */

const HEALTH_URL = 'https://tileforge-api.echoforge.games/health';
const POLL_INTERVAL_ONLINE = 60 * 1000;   // Check every 60s when online
const POLL_INTERVAL_OFFLINE = 15 * 1000;  // Check every 15s when offline
const HEALTH_TIMEOUT = 5000;              // 5s timeout for health check
const QUEUE_DB_NAME = 'tileforge-sync-queue';
const QUEUE_DB_VERSION = 1;
const QUEUE_STORE = 'pending';

export class ConnectionMonitor {
  constructor() {
    this._online = navigator.onLine;
    this._apiReachable = false;
    this._listeners = [];
    this._timer = null;
    this._queueDb = null;
    this._syncing = false;
    this._pendingCount = 0;
  }

  /** Current connectivity state. */
  get isOnline() { return this._online && this._apiReachable; }

  /** True if we have queued operations waiting to sync. */
  get hasPending() { return this._pendingCount > 0; }

  /** Register a state change listener. */
  onChange(fn) {
    this._listeners.push(fn);
    return () => { this._listeners = this._listeners.filter(f => f !== fn); };
  }

  /** Start monitoring. Call once at boot. */
  async start() {
    await this._openQueueDB();

    // Browser online/offline events (fast, but unreliable for true API connectivity)
    window.addEventListener('online', () => this._check());
    window.addEventListener('offline', () => {
      this._online = false;
      this._apiReachable = false;
      this._notify();
      this._reschedule();
    });

    // Visibility-aware: check immediately when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this._check();
    });

    // Initial check
    await this._check();
    this._reschedule();
  }

  /** Stop monitoring. */
  stop() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
  }

  /**
   * Queue a failed cloud operation for retry when back online.
   * @param {string} type — 'upload' | 'delete'
   * @param {Object} payload — { path, data (as ArrayBuffer), contentType }
   */
  async queueOperation(type, payload) {
    if (!this._queueDb) return;
    const record = {
      id: `${type}:${payload.path}:${Date.now()}`,
      type,
      path: payload.path,
      contentType: payload.contentType,
      data: payload.data instanceof ArrayBuffer ? payload.data : null,
      queuedAt: Date.now(),
    };
    await this._queuePut(record);
    this._pendingCount = (this._pendingCount || 0) + 1;
    this._notify();
  }

  /**
   * Process all queued operations. Called automatically on reconnect.
   * @param {Function} uploadFn — async (path, data, contentType) => result
   */
  async processQueue(uploadFn) {
    if (this._syncing || !this._queueDb) return;
    this._syncing = true;
    this._notify();

    try {
      const items = await this._queueGetAll();
      for (const item of items) {
        if (!this.isOnline) break; // Stop if we went offline again

        try {
          if (item.type === 'upload' && item.data) {
            await uploadFn(item.path, item.data, item.contentType);
          }
          await this._queueDelete(item.id);
          this._pendingCount = Math.max(0, (this._pendingCount || 0) - 1);
        } catch (err) {
          // Track retry count — drop permanently failing items after 3 attempts
          const retries = (item.retries || 0) + 1;
          if (retries >= 3) {
            console.warn(`[Sync] Dropping queued ${item.type} for ${item.path} after ${retries} failures`);
            await this._queueDelete(item.id);
            this._pendingCount = Math.max(0, (this._pendingCount || 0) - 1);
          } else {
            // Update retry count in queue
            await this._queuePut({ ...item, retries });
            console.warn(`[Sync] Retry ${retries}/3 for ${item.path}:`, err.message);
          }
          continue; // Try next item instead of stopping
        }
      }
    } finally {
      this._syncing = false;
      this._notify();
    }
  }

  get isSyncing() { return this._syncing; }

  // ─── Internal ──────────────────────────────────────────────────

  async _check() {
    this._online = navigator.onLine;
    if (!this._online) {
      this._apiReachable = false;
      this._notify();
      return;
    }

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), HEALTH_TIMEOUT);
      const res = await fetch(HEALTH_URL, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeout);
      const wasReachable = this._apiReachable;
      this._apiReachable = res.ok;

      if (this._apiReachable && !wasReachable) {
        // Just came back online — process queue
        this._notify();
        // Queue processing is triggered by listeners (CloudStorage wires this)
      } else {
        this._notify();
      }
    } catch {
      this._apiReachable = false;
      this._notify();
    }

    this._reschedule();
  }

  _reschedule() {
    if (this._timer) clearTimeout(this._timer);
    const interval = this.isOnline ? POLL_INTERVAL_ONLINE : POLL_INTERVAL_OFFLINE;
    this._timer = setTimeout(() => this._check(), interval);
  }

  _notify() {
    const state = {
      online: this.isOnline,
      apiReachable: this._apiReachable,
      networkUp: this._online,
      syncing: this._syncing,
      pendingCount: this._pendingCount || 0,
    };
    for (const fn of this._listeners) {
      try { fn(state); } catch (e) { console.error('ConnectionMonitor listener error:', e); }
    }
  }

  // ─── Queue DB ──────────────────────────────────────────────────

  async _openQueueDB() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(QUEUE_DB_NAME, QUEUE_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(QUEUE_STORE)) {
          db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => {
        this._queueDb = req.result;
        // Count existing pending items
        const tx = this._queueDb.transaction(QUEUE_STORE, 'readonly');
        const countReq = tx.objectStore(QUEUE_STORE).count();
        countReq.onsuccess = () => { this._pendingCount = countReq.result; };
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  }

  _queuePut(record) {
    return new Promise((resolve, reject) => {
      const tx = this._queueDb.transaction(QUEUE_STORE, 'readwrite');
      tx.objectStore(QUEUE_STORE).put(record);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  }

  _queueDelete(id) {
    return new Promise((resolve, reject) => {
      const tx = this._queueDb.transaction(QUEUE_STORE, 'readwrite');
      tx.objectStore(QUEUE_STORE).delete(id);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  }

  _queueGetAll() {
    return new Promise((resolve, reject) => {
      const tx = this._queueDb.transaction(QUEUE_STORE, 'readonly');
      const req = tx.objectStore(QUEUE_STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }
}
